import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { IntakeItemDto, LeadIntakeDto, SitePlace } from '@nsb/core';
import { api, errorText, queryClient, useApi } from '../api.ts';
import { PlaceInput } from '../components/PlaceInput.tsx';
import { TestProjectBox } from '../components/TestProject.tsx';
import { Button, ErrorLine, Field, Loading, Page, TextInput, Title } from '../components/ui.tsx';

/**
 * Three ways in. A new project is its title and goal alone, started at once
 * (issue #3, Jason: "Starting a new project should be ultra light weight, just
 * the title, goal"; 2026-10-05: "create the project simply and then let needs
 * drive additional configuration"): its other details are added from its
 * home as they are needed. A new lead is a name, a contact, a site and a source note (spec
 * §8), and its goal. A lead read from a forwarded email is checked before it
 * is added (?intake=).
 */
export function NewLead() {
  const [params] = useSearchParams();
  const intakeId = params.get('intake');
  const direct = params.get('project') === '1';
  const { data: intake } = useApi<LeadIntakeDto>(['leads', 'intake'], intakeId ? '/leads/intake' : null);
  if (direct && !intakeId) return <ProjectForm />;
  if (!intakeId) return <LeadForm />;
  if (!intake) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const item = intake.items.find((i) => i.id === intakeId);
  if (!item) {
    return (
      <Page>
        <Title kicker="Lead from email">Already dealt with</Title>
        <p className="text-ink-2">That email has been added or discarded already.</p>
      </Page>
    );
  }
  return <LeadForm fromEmail={item} />;
}

const GOAL_HINT = 'What it is for, in a sentence or two: the building, the place, the timing. Its picture is painted from this.';

/** A new project: what it is called and what it is for, and it is started. */
function ProjectForm() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [isSample, setIsSample] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const { id } = await api<{ id: string }>('/projects', { body: { name: name.trim(), goal: goal.trim() || null, isSample } });
      await queryClient.invalidateQueries({ queryKey: ['pipeline'] });
      navigate(`/p/${id}`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Page>
      <Title kicker="New project">What is it?</Title>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void start();
        }}
      >
        <Field label="Title">{(id) => <TextInput id={id} required autoFocus value={name} onChange={(e) => setName(e.target.value)} />}</Field>
        <Field label="Goal" hint={GOAL_HINT}>
          {(id) => <textarea id={id} rows={3} className="field resize-y" value={goal} onChange={(e) => setGoal(e.target.value)} />}
        </Field>
        <TestProjectBox checked={isSample} onChange={setIsSample} />
        <ErrorLine error={error} />
        <div className="mt-8">
          <Button type="submit" busy={busy} disabled={!name.trim()}>
            Start the project
          </Button>
        </div>
      </form>
    </Page>
  );
}

function LeadForm({ fromEmail }: { fromEmail?: IntakeItemDto }) {
  const navigate = useNavigate();
  const p = fromEmail?.proposal;
  const [f, setF] = useState({
    name: p?.name ?? '',
    contactName: p?.contactName ?? '',
    contactEmail: p?.contactEmail ?? '',
    contactPhone: p?.contactPhone ?? '',
    siteAddress: p?.site ?? '',
    sourceNote: p?.sourceNote ?? '',
    summary: p?.summary ?? '',
  });
  const [sitePlace, setSitePlace] = useState<SitePlace | null>(null);
  const [isSample, setIsSample] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  const save = async (startNow: boolean) => {
    setBusy(true);
    setError(null);
    const lead = {
      name: f.name,
      contactName: f.contactName || null,
      contactEmail: f.contactEmail || null,
      contactPhone: f.contactPhone || null,
      siteAddress: f.siteAddress || null,
      sitePlace,
      sourceNote: f.sourceNote || null,
      summary: f.summary.trim() || null,
    };
    try {
      // Saved and started in one call (B40): a try that fails leaves nothing behind, so trying again makes one lead.
      const { id } = fromEmail
        ? await api<{ id: string }>(`/leads/intake/${fromEmail.id}/accept`, { body: { ...lead, start: startNow } })
        : await api<{ id: string }>('/leads', { body: { ...lead, isSample, start: startNow } });
      await Promise.all([queryClient.invalidateQueries({ queryKey: ['pipeline'] }), queryClient.invalidateQueries({ queryKey: ['leads', 'intake'] })]);
      navigate(startNow ? `/p/${id}` : '/');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <Title kicker={fromEmail ? 'Lead from email' : 'New lead'}>Who is it?</Title>
      {fromEmail ? (
        <p className="-mt-2 mb-6 text-[0.875rem] text-ink-2">
          Read from “{fromEmail.subject || 'an email'}”{fromEmail.from ? `, from ${fromEmail.from}` : ''}. Check it before it is added.
        </p>
      ) : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save(false);
        }}
      >
        <Field label="Name" hint="The project or the person.">
          {(id) => <TextInput id={id} required autoFocus value={f.name} onChange={set('name')} />}
        </Field>
        <Field label="Goal" hint={GOAL_HINT}>
          {(id) => <textarea id={id} rows={3} className="field resize-y" value={f.summary} onChange={set('summary')} />}
        </Field>
        <Field label="Contact">{(id) => <TextInput id={id} value={f.contactName} onChange={set('contactName')} placeholder="Name" />}</Field>
        <div className="grid gap-x-6 sm:grid-cols-2">
          <Field label="Email">{(id) => <TextInput id={id} type="email" value={f.contactEmail} onChange={set('contactEmail')} />}</Field>
          <Field label="Phone">{(id) => <TextInput id={id} type="tel" value={f.contactPhone} onChange={set('contactPhone')} />}</Field>
        </div>
        <Field label="Site address">
          {(id) => (
            <PlaceInput
              id={id}
              value={f.siteAddress}
              place={sitePlace}
              onChange={(text, place) => {
                setF({ ...f, siteAddress: text });
                setSitePlace(place);
              }}
            />
          )}
        </Field>
        <Field label="Source" hint="How they found NSB.">
          {(id) => <TextInput id={id} value={f.sourceNote} onChange={set('sourceNote')} />}
        </Field>
        {fromEmail ? null : (
          <TestProjectBox checked={isSample} onChange={setIsSample} />
        )}
        <ErrorLine error={error} />
        <div className="mt-8 flex flex-wrap items-center gap-6">
          <Button type="submit" busy={busy} disabled={!f.name.trim()}>
            {fromEmail ? 'Add lead' : 'Save lead'}
          </Button>
          <Button tone="quiet" disabled={!f.name.trim() || busy} onClick={() => void save(true)}>
            {fromEmail ? 'Add it and start the project' : 'Save and start the project'}
          </Button>
        </div>
      </form>
    </Page>
  );
}
